import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Select from "react-select";
import Footer from "./Footer";
import "./Address.css";
import {BASE_URL} from "./Constants";

const Address = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    address_type: "",
    gst_number: "",
    company_name: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_no: "",
    country: "",
    street: "",
    city: "",
    state: "",
    pin: "",
    ship_different: false,
    order_notes: ""
  });

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(null); 
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const addressOptions = [
    { value: "Home", label: "Home" },
    { value: "Office", label: "Office" },
    { value: "Other", label: "Other" }
  ];

  // Fetch logged-in user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/get_user_details`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Not logged in");
        const userData = await res.json();
        setUser(userData); 
      } catch (err) {
        console.log("No user logged in:", err.message);
        setUser(null);
        navigate("/checkout/login");
      }
    };
    fetchUser();
  }, [navigate]);

  // Fetch saved addresses
  useEffect(() => {
    if (!user) return;

    const fetchAddresses = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/fetch_address?user_id=${user.user_id}`,
          { 
            method: "GET", 
            credentials: "include" ,
            headers: {
            "Content-Type": "application/json"
          }
          }
        );

        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setSavedAddresses(data?.addresses || []);

        // const addresses = await res.json();
        // setSavedAddresses(Array.isArray(addresses) ? addresses : addresses?.data || []);
      } catch (err) {
        console.error("Error fetching addresses:", err);
      }
    };

    fetchAddresses();
  }, [user]);

  const handleSelectChange = (selectedOption) => {
    setForm(prev => ({ ...prev, address_type: selectedOption ? selectedOption.value : "" }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSelectSavedAddress = (index) => {
    const addr = savedAddresses[index] || {};
    setForm({ ...addr, ship_different: addr.ship_different || false });
    setSelectedAddressIndex(index);
    setSelectedAddressId(addr.id || null);
  };

  const handleClearSavedAddress = () => {
    setForm({
      address_type: "",
      gst_number: "",
      company_name: "",
      first_name: "",
      last_name: "",
      email: "",
      phone_no: "",
      country: "",
      street: "",
      city: "",
      state: "",
      pin: "",
      ship_different: false,
      order_notes: ""
    });
    setSelectedAddressIndex(null);
    setSelectedAddressId(null);
  };

  const handleContinue = async () => {
    if (!user) {
      alert("User not logged in");
      navigate("/checkout/login");
      return;
    }

    const requiredFields = ["address_type","first_name","last_name","email","phone_no","country","street","city","state","pin"];
    for (let field of requiredFields) {
      if (!form[field]) {
        // alert("Please fill all required fields.");
        setMessage("Please fill all required fields.")
        return;
      }
    }

    try {
      if (selectedAddressId) {
        // Update existing address
        const response = await fetch(
          `${BASE_URL}/update_address/${selectedAddressId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(form)
          }
        );

        if (!response.ok) throw new Error("Failed to update address");

        const updatedAddress = await response.json();
        const updatedList = savedAddresses.map((addr, idx) =>
          idx === selectedAddressIndex ? updatedAddress.data : addr
        );
        setSavedAddresses(updatedList);
        // alert("Address updated successfully!");
        setMessage("Address updated successfully!")
      } else {
        // Add new address
        const payload = { user_id: user.user_id, addresses: [form] };
        const response = await fetch(`${BASE_URL}/add_address`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error("Failed to save address");

        const newAddress = await response.json();
        setSavedAddresses([...savedAddresses.filter(a => a), newAddress.data]);
        // alert("Address added successfully!");
        setMessage("Address updated successfully!")
      }

      navigate("/checkout/placeorder");
    } catch (err) {
      // alert("Error saving address: " + err.message);
      setMessageType("Error saving address: " + err.message)
    }
  };

  return (
    <div>
      <h2 className="checkout-title">Address</h2>
      <div className="address-form">
        {savedAddresses.length > 0 && (
          <div className="saved-address-list">
            <h3>Choose a Saved Address</h3>
            <Select
              options={savedAddresses
                .filter(addr => addr)
                .map((addr, index) => ({
                  value: index,
                  label: `${addr.address_type} - ${addr.first_name} ${addr.last_name}, ${addr.street}, ${addr.city}, ${addr.state}, ${addr.pin}, ${addr.country}, ${addr.phone_no}`
                }))}
              placeholder="Select an Address"
              value={selectedAddressIndex !== null && savedAddresses[selectedAddressIndex] 
                ? { value: selectedAddressIndex, label: `${savedAddresses[selectedAddressIndex].address_type} - ${savedAddresses[selectedAddressIndex].first_name} ${savedAddresses[selectedAddressIndex].last_name}` } 
                : null}
              onChange={(selected) =>
                selected ? handleSelectSavedAddress(selected.value) : handleClearSavedAddress()
              }
              isClearable
              menuPlacement="bottom"
            />
          </div>
        )}

        <h2>Billing Details</h2>
        <div className="form-grid">
          {["address_type","gst_number","company_name","first_name","last_name","email","country","phone_no","city","street","pin","state"].map(field => (
            <div className="form-group" key={field}>
              <label>
                {field.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                {["first_name","last_name","email","country","phone_no","city","street","pin","state"].includes(field) ? "*" : ""}
              </label>
              {field === "address_type" ? (
                <Select
                  options={addressOptions}
                  value={form.address_type ? addressOptions.find(opt => opt.value === form.address_type) : null}
                  onChange={handleSelectChange}
                  menuPlacement="bottom"
                  isClearable
                />
              ) : (
                <input
                  type={field === "email" ? "email" : "text"}
                  name={field}
                  value={form[field] || ""}
                  onChange={handleChange}
                  required={["first_name","last_name","email","country","phone_no","city","street","pin","state"].includes(field)}
                />
              )}
            </div>
          ))}
        </div>

        <h3>Shipping Details</h3>
        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            name="ship_different"
            checked={form.ship_different}
            onChange={handleChange}
          />
          <label>Ship to different address?</label>
        </div>

        <div className="form-group full-width">
          <label>Order notes (optional)</label>
          <textarea
            name="order_notes"
            value={form.order_notes}
            onChange={handleChange}
            placeholder="Notes about your order"
          />
        </div>

        {message && (
          <p className={`msg ${messageType}`}>{message}</p>
        )}

        <button type="submit" className="continue-btn" onClick={handleContinue}>
          Continue to Payment
        </button>
      </div>
      <Footer />
    </div>
  );
};

export default Address;
